import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApp, api } from '@/context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Play, Pause, Volume2, FileText, Hand, RotateCw, Clock,
  Shield, Wrench, Target, AlertTriangle, CheckCircle2, Hash,
  Sparkles, Activity, ChevronRight, Box,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ExerciseDetailPage() {
  const { id } = useParams();
  const { t, lang } = useApp();
  const [exo, setExo] = useState(null);
  const [audioBusy, setAudioBusy] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const [slow, setSlow] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => { api.get(`/exercises/${id}`).then(r => setExo(r.data)).catch(() => setExo(null)); }, [id]);

  // Auto-cycle steps as a "slow play" demo when autoPlay is on
  useEffect(() => {
    if (!autoPlay || !exo) return;
    const steps = (lang === 'ar' ? exo.steps_ar : (exo.steps_en?.length ? exo.steps_en : exo.steps_ar)) || [];
    if (steps.length === 0) return;
    const interval = slow ? 4500 : 2200;
    const id = setInterval(() => setActiveStep(s => (s + 1) % steps.length), interval);
    return () => clearInterval(id);
  }, [autoPlay, slow, exo, lang]);

  const speak = async () => {
    if (!exo) return;
    if (exo[`audio_url_${lang}`]) {
      audioRef.current?.play();
      return;
    }
    setAudioBusy(true);
    try {
      const r = await api.post(`/exercises/${id}/audio?language=${lang}`);
      setExo({ ...exo, [`audio_url_${lang}`]: r.data.audio_url });
      setTimeout(() => audioRef.current?.play(), 300);
    } catch (e) { toast.error(e.response?.data?.detail || 'Audio failed'); }
    setAudioBusy(false);
  };

  const exportPdf = () => {
    if (!exo) return;
    const isAr = lang === 'ar';
    const w = window.open('', '_blank'); if (!w) return;
    const steps = (isAr ? exo.steps_ar : (exo.steps_en?.length ? exo.steps_en : exo.steps_ar)) || [];
    const errs = exo.common_errors || [];
    w.document.write(`<!doctype html><html dir='${isAr ? 'rtl' : 'ltr'}'><head><meta charset='utf-8'><title>${exo.name_ar}</title>
      <style>
        body { font-family: 'Cairo','Segoe UI',sans-serif; margin: 30px; color:#1C1917; }
        .hdr { border-bottom: 3px double #4F46E5; padding-bottom: 12px; margin-bottom: 20px; }
        h1 { color: #4F46E5; margin: 0 0 6px 0; }
        h2 { color: #B45309; border-${isAr ? 'right' : 'left'}: 4px solid #B45309; padding-${isAr ? 'right' : 'left'}: 10px; margin-top: 22px; }
        .pill { display: inline-block; padding: 3px 10px; border-radius: 999px; background: #4F46E5; color: white; font-size: 11px; margin-${isAr ? 'left' : 'right'}: 6px; }
        ol, ul { line-height: 1.8; }
        .err { background: #FEF2F2; border-left: 4px solid #DC2626; padding: 8px 12px; margin: 6px 0; border-radius: 6px; }
        .footer { margin-top: 30px; border-top: 2px solid #14532D; padding-top: 10px; font-size: 11px; color: #6B7280; text-align:center; }
        @media print { body { margin: 0; padding: 14mm; } .tb { display: none; } }
        .tb button { background: #4F46E5; color: white; padding: 8px 16px; border: none; border-radius: 8px; font-weight: 800; cursor: pointer; }
      </style></head><body>
      <div class='tb'><button onclick='window.print()'>${isAr ? '🖨 طباعة / PDF' : '🖨 Print / PDF'}</button></div>
      <div class='hdr'>
        <h1>${isAr ? exo.name_ar : (exo.name_en || exo.name_ar)}</h1>
        <div style='margin-top:6px;'>
          <span class='pill'>${isAr ? exo.skill_type_ar : (exo.skill_type_en || exo.skill_type_ar)}</span>
          <span class='pill'>${exo.level}</span>
          <span class='pill'>${exo.age_min}–${exo.age_max} ${isAr ? 'سنة' : 'yrs'}</span>
        </div>
      </div>
      <h2>${isAr ? '🎯 الهدف' : '🎯 Objective'}</h2>
      <p>${isAr ? exo.objective_ar : (exo.objective_en || exo.objective_ar)}</p>
      <h2>${isAr ? '🛠 الأدوات' : '🛠 Tools'}</h2>
      <p>${isAr ? (exo.tools_ar || '—') : (exo.tools_en || exo.tools_ar || '—')}</p>
      <h2>${isAr ? '👣 خطوات الأداء' : '👣 Steps'}</h2>
      <ol>${steps.map(s => `<li>${s}</li>`).join('')}</ol>
      <h2>${isAr ? '🛡 السلامة' : '🛡 Safety'}</h2>
      <p>${isAr ? (exo.safety_ar || '—') : (exo.safety_en || exo.safety_ar || '—')}</p>
      <h2>${isAr ? '✅ معايير النجاح' : '✅ Success Criteria'}</h2>
      <p>${isAr ? (exo.success_criteria_ar || '—') : (exo.success_criteria_en || exo.success_criteria_ar || '—')}</p>
      <h2>${isAr ? '⚠ أخطاء شائعة وتصحيحها' : '⚠ Common Errors & Fixes'}</h2>
      ${errs.map(e => `<div class='err'><b>${isAr ? e.error_ar : (e.error_en || e.error_ar)}</b><br><i>${isAr ? e.fix_ar : (e.fix_en || e.fix_ar)}</i></div>`).join('') || '—'}
      <h2>${isAr ? '🔧 تعديلات حسب الإعاقة' : '🔧 Modifications by Disability'}</h2>
      <ul>${Object.entries(exo.modifications || {}).map(([k, v]) => `<li><b>${k}:</b> ${v}</li>`).join('') || '—'}</ul>
      <div class='footer'>${isAr ? 'منصة همم — مكتبة التمارين الحركية الذكية' : 'Himam Platform — Smart Movement Library'} · ${new Date().toLocaleDateString(isAr ? 'ar-AE' : 'en-US')}</div>
      </body></html>`);
    w.document.close();
  };

  if (!exo) return <main id="main" className="p-8 text-center text-muted-foreground" data-testid="exo-loading">{t('loading')}</main>;

  const steps = (lang === 'ar' ? exo.steps_ar : (exo.steps_en?.length ? exo.steps_en : exo.steps_ar)) || [];
  const audioUrl = exo[`audio_url_${lang}`] ? `${process.env.REACT_APP_BACKEND_URL}${exo[`audio_url_${lang}`]}` : null;

  return (
    <main id="main" className="max-w-7xl mx-auto px-4 py-6" data-testid="exo-detail">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/exercises"><ArrowLeft className={`w-4 h-4 me-1 ${lang === 'ar' ? 'rotate-180' : ''}`} />{lang === 'ar' ? 'المكتبة' : 'Library'}</Link>
      </Button>

      {/* Header */}
      <section className="bg-gradient-to-br from-indigo-50 via-violet-50 to-emerald-50 border-2 border-indigo-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Badge className="bg-indigo-600 text-white text-[11px]">{lang === 'ar' ? exo.skill_type_ar : (exo.skill_type_en || exo.skill_type_ar)}</Badge>
          <Badge variant="outline" className="text-[11px]">{exo.level}</Badge>
          <Badge variant="outline" className="text-[11px]">{exo.age_min}–{exo.age_max} {lang === 'ar' ? 'سنة' : 'yrs'}</Badge>
          {exo.ai_generated && <Badge className="bg-amber-500 text-white text-[11px]"><Sparkles className="w-3 h-3 me-0.5" />AI generated</Badge>}
        </div>
        <h1 className="text-2xl sm:text-3xl font-black mb-2">{lang === 'ar' ? exo.name_ar : (exo.name_en || exo.name_ar)}</h1>
        <p className="text-sm text-muted-foreground max-w-3xl">{lang === 'ar' ? exo.objective_ar : (exo.objective_en || exo.objective_ar)}</p>

        {/* Accessibility tools row */}
        <div className="flex items-center gap-2 mt-5 flex-wrap" data-testid="exo-tools">
          <Button size="sm" variant={autoPlay ? 'default' : 'outline'} onClick={() => setAutoPlay(p => !p)} className={autoPlay ? 'bg-indigo-600' : ''} data-testid="play-steps">
            {autoPlay ? <Pause className="w-4 h-4 me-1" /> : <Play className="w-4 h-4 me-1" />}
            {lang === 'ar' ? 'تشغيل الخطوات' : 'Play steps'}
          </Button>
          <Button size="sm" variant={slow ? 'default' : 'outline'} onClick={() => setSlow(s => !s)} className={slow ? 'bg-indigo-600' : ''} data-testid="slow-play">
            <Clock className="w-4 h-4 me-1" />{lang === 'ar' ? 'تشغيل بطيء' : 'Slow motion'}
          </Button>
          <Button size="sm" variant="outline" onClick={speak} disabled={audioBusy} data-testid="play-audio">
            <Volume2 className="w-4 h-4 me-1" />{audioBusy ? (lang === 'ar' ? 'إنشاء...' : 'Generating...') : (lang === 'ar' ? 'وصف صوتي' : 'Audio description')}
          </Button>
          <Button size="sm" variant="outline" data-testid="sign-lang" onClick={() => {
            const u = exo.sign_language_video_url || 'https://www.youtube.com/results?search_query=' + encodeURIComponent((lang === 'ar' ? exo.name_ar : exo.name_en) + ' sign language');
            window.open(u, '_blank');
          }}>
            <Hand className="w-4 h-4 me-1" />{lang === 'ar' ? 'لغة الإشارة' : 'Sign language'}
          </Button>
          <Button size="sm" variant="outline" onClick={exportPdf} data-testid="export-pdf">
            <FileText className="w-4 h-4 me-1" />{lang === 'ar' ? 'تحميل PDF' : 'Download PDF'}
          </Button>
          {audioUrl && <audio ref={audioRef} src={audioUrl} controls className="ms-2 h-9" data-testid="audio-player" />}
        </div>
      </section>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main column: 3D + steps + tabs */}
        <div className="lg:col-span-2 space-y-6">
          {/* 3D / Visual sequence */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base"><Box className="w-5 h-5 text-indigo-600" />{lang === 'ar' ? 'نموذج حركي تفاعلي' : 'Interactive Motion Model'}</CardTitle>
              <Badge variant="secondary" className="text-[10px]">{lang === 'ar' ? 'اسحب للتدوير' : 'Drag to rotate'}</Badge>
            </CardHeader>
            <CardContent>
              <Rotatable3D step={activeStep} total={steps.length || 1} skill={exo.skill_type_ar} />
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4" data-testid="step-thumbs">
                {steps.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setActiveStep(i); setAutoPlay(false); }}
                    data-testid={`step-${i}`}
                    className={`p-3 rounded-lg border-2 text-xs text-start transition-all ${i === activeStep ? 'border-indigo-600 bg-indigo-50' : 'border-border bg-white hover:border-indigo-300'}`}
                  >
                    <div className="text-[10px] font-bold text-indigo-600 mb-1">{lang === 'ar' ? `الخطوة ${i + 1}` : `Step ${i + 1}`}</div>
                    <div className="line-clamp-3 leading-snug">{s}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Details tabs */}
          <Card>
            <Tabs defaultValue="steps">
              <CardHeader className="pb-2">
                <TabsList className="bg-secondary/60">
                  <TabsTrigger value="steps" data-testid="tab-steps"><Hash className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'الخطوات' : 'Steps'}</TabsTrigger>
                  <TabsTrigger value="errors" data-testid="tab-errors"><AlertTriangle className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'الأخطاء' : 'Errors'}</TabsTrigger>
                  <TabsTrigger value="modifications" data-testid="tab-mods"><Sparkles className="w-3.5 h-3.5 me-1" />{lang === 'ar' ? 'تعديلات' : 'Mods'}</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent>
                <TabsContent value="steps">
                  <ol className="space-y-2">
                    {steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg" data-testid={`step-item-${i}`}>
                        <span className="w-6 h-6 rounded-full bg-indigo-600 text-white font-black text-xs grid place-items-center shrink-0">{i + 1}</span>
                        <span className="text-sm leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </TabsContent>
                <TabsContent value="errors">
                  <ul className="space-y-2" data-testid="errors-list">
                    {(exo.common_errors || []).length === 0 ? <p className="text-sm text-muted-foreground">—</p> :
                      (exo.common_errors || []).map((e, i) => (
                        <li key={i} className="border-l-4 border-rose-500 bg-rose-50 rounded p-3">
                          <div className="text-sm font-bold text-rose-700">{lang === 'ar' ? e.error_ar : (e.error_en || e.error_ar)}</div>
                          <div className="text-sm text-stone-700 mt-1">→ {lang === 'ar' ? e.fix_ar : (e.fix_en || e.fix_ar)}</div>
                        </li>
                      ))}
                  </ul>
                </TabsContent>
                <TabsContent value="modifications">
                  <ul className="space-y-2" data-testid="mods-list">
                    {Object.entries(exo.modifications || {}).map(([k, v]) => (
                      <li key={k} className="border border-border rounded p-3 bg-white">
                        <Badge variant="secondary" className="text-[10px] mb-1 uppercase">{k}</Badge>
                        <div className="text-sm">{v}</div>
                      </li>
                    ))}
                    {(exo.lesson_plan_ar || exo.lesson_plan_en) && (
                      <li className="border-2 border-indigo-200 rounded-lg p-3 bg-indigo-50/50">
                        <Badge className="bg-indigo-600 text-[10px] mb-1">{lang === 'ar' ? 'خطة درس' : 'Lesson plan'}</Badge>
                        <div className="text-sm whitespace-pre-wrap">{lang === 'ar' ? exo.lesson_plan_ar : exo.lesson_plan_en}</div>
                      </li>
                    )}
                    {(exo.home_activity_ar || exo.home_activity_en) && (
                      <li className="border-2 border-emerald-200 rounded-lg p-3 bg-emerald-50/50">
                        <Badge className="bg-emerald-600 text-[10px] mb-1">{lang === 'ar' ? 'نشاط منزلي' : 'Home activity'}</Badge>
                        <div className="text-sm">{lang === 'ar' ? exo.home_activity_ar : exo.home_activity_en}</div>
                      </li>
                    )}
                  </ul>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <InfoCard icon={Target} title={lang === 'ar' ? 'الهدف' : 'Objective'} text={lang === 'ar' ? exo.objective_ar : (exo.objective_en || exo.objective_ar)} />
          <InfoCard icon={Wrench} title={lang === 'ar' ? 'الأدوات' : 'Tools'} text={lang === 'ar' ? (exo.tools_ar || '—') : (exo.tools_en || exo.tools_ar || '—')} />
          <InfoCard icon={Shield} title={lang === 'ar' ? 'السلامة' : 'Safety'} text={lang === 'ar' ? (exo.safety_ar || '—') : (exo.safety_en || exo.safety_ar || '—')} color="text-rose-600" />
          <InfoCard icon={CheckCircle2} title={lang === 'ar' ? 'معايير النجاح' : 'Success criteria'} text={lang === 'ar' ? (exo.success_criteria_ar || '—') : (exo.success_criteria_en || exo.success_criteria_ar || '—')} color="text-emerald-600" />

          {/* Quick-link to video analyzer */}
          <Card className="bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200">
            <CardContent className="p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-violet-700 mb-1">{lang === 'ar' ? 'تقييم أداء طالب' : 'Evaluate student'}</div>
              <div className="text-sm mb-3">{lang === 'ar' ? 'ارفع فيديو أداء وسيقوم الذكاء الاصطناعي بمقارنته بالنموذج وإعطاء ملاحظات.' : 'Upload student video and let AI compare and feedback.'}</div>
              <Button asChild size="sm" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="goto-analyzer">
                <Link to={`/exercises/analyze?ex=${exo.id}`}>
                  <Activity className="w-4 h-4 me-1" />{lang === 'ar' ? 'بدء التحليل' : 'Start review'}
                  <ChevronRight className="w-4 h-4 ms-auto rtl:rotate-180" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}

function InfoCard({ icon: Icon, title, text, color = 'text-indigo-600' }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center gap-2 mb-1.5"><Icon className={`w-4 h-4 ${color}`} /><div className="font-bold text-xs uppercase tracking-wider text-muted-foreground">{title}</div></div>
      <div className="text-sm leading-relaxed">{text}</div>
    </CardContent></Card>
  );
}

// Lightweight 3D-look figure: a CSS-rotated avatar that the user can drag to rotate.
// The pose library maps exercise-name keywords + skill type to a sequence of
// articulated SVG poses that visually communicate THIS exercise (not generic).
function Rotatable3D({ step, total, skill }) {
  const [rotY, setRotY] = useState(15);
  const [rotX, setRotX] = useState(-5);
  const dragRef = useRef(null);
  const figureRef = useRef(null);

  const onPointerDown = (e) => { dragRef.current = { x: e.clientX, y: e.clientY, rY: rotY, rX: rotX }; };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setRotY(dragRef.current.rY + dx * 0.5);
    setRotX(Math.max(-30, Math.min(30, dragRef.current.rX + dy * 0.3)));
  };
  const onPointerUp = () => { dragRef.current = null; };
  const reset = () => { setRotY(15); setRotX(-5); };
  const hue = { 'توازن': 210, 'قوة': 0, 'مرونة': 290, 'تآزر': 160, 'إدراك مكاني': 45, 'مهارة رياضية': 250 }[skill] ?? 230;

  return (
    <div className="relative w-full bg-gradient-to-br from-stone-100 to-indigo-100/50 rounded-xl overflow-hidden select-none" data-testid="model-3d"
         style={{ height: '340px', perspective: '900px' }}
         onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
      {/* Floor grid for spatial reference */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none" style={{
        background: 'linear-gradient(to top, rgba(79,70,229,0.10), transparent)',
        transform: `rotateX(60deg) translateY(40px)`,
        transformOrigin: 'center bottom',
        backgroundImage: 'linear-gradient(rgba(79,70,229,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(79,70,229,0.2) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />
      <div className="absolute inset-0 grid place-items-center" style={{ transformStyle: 'preserve-3d' }}>
        <div
          ref={figureRef}
          className="relative transition-transform duration-500 ease-out"
          style={{ transform: `rotateY(${rotY}deg) rotateX(${rotX}deg)`, transformStyle: 'preserve-3d', width: '160px', height: '260px' }}
        >
          <FigureSVG hue={hue} pose={poseKeyForStep(skill, step, total)} />
        </div>
      </div>
      <div className="absolute top-3 start-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow text-xs font-bold text-indigo-700 border border-indigo-200" data-testid="model-step-label">
        Step {step + 1} / {total}
      </div>
      <button onClick={reset} className="absolute top-3 end-3 bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow text-indigo-700 hover:bg-indigo-50" data-testid="reset-3d" aria-label="Reset rotation">
        <RotateCw className="w-4 h-4" />
      </button>
      <div className="absolute bottom-2 inset-x-0 text-center text-[10px] text-stone-500">↻ Drag to rotate · ↑↓ tilt · figure auto-poses with each step</div>
    </div>
  );
}

/* === POSE LIBRARY ===
   Each pose returns an SVG body using a normalised 200×320 viewBox so all
   poses align on the floor. Body parts: head, torso, arms, legs. Stroke
   matches the skill hue, fills hint a soft body.

   We use a richer pose set than the previous 4-frame cycle, keyed by step %
   per skill type so balance / strength / coordination / flexibility / spatial
   / sport-skill exercises actually look distinct.
*/
const POSE_SETS = {
  // BALANCE — standing → leg lift → hold → mini wobble → return
  'توازن': ['stand', 'leg-lift-right', 'leg-lift-hold', 'leg-lift-arms', 'stand'],
  // STRENGTH — squat / sit-to-stand cycle
  'قوة': ['squat-low', 'squat-mid', 'stand-arms', 'squat-mid', 'squat-low'],
  // FLEXIBILITY — cat-cow / fold cycle
  'مرونة': ['quadruped', 'arch-up', 'arch-down', 'forward-fold', 'stand'],
  // COORDINATION — sitting → reach → throw cycle
  'تآزر': ['sit', 'sit-reach', 'sit-throw', 'sit', 'stand-arms'],
  // SPATIAL — crawling along line
  'إدراك مكاني': ['quadruped', 'crawl-right', 'crawl-left', 'crawl-right', 'quadruped'],
  // SPORT-SKILL — throwing motion
  'مهارة رياضية': ['stand-arms', 'wind-up', 'throw-release', 'follow-through', 'stand'],
};

// Pose to articulated path/circle blocks. Coordinates assume a 200×320 canvas.
// Head=18r circle at (cx,cy); body = head → neck → torso → hips with arms+legs.
function FigureSVG({ hue, pose }) {
  const stroke = `hsl(${hue}, 70%, 35%)`;
  const fill = `hsl(${hue}, 80%, 60%)`;
  const skin = `hsl(${hue}, 80%, 78%)`;

  // Determine the pose: pose can be either an index into POSE_SETS or a string key.
  // We default to "stand" if nothing matches.
  const poseKey = typeof pose === 'string' ? pose : 'stand';
  const P = POSES_BY_KEY[poseKey] || POSES_BY_KEY['stand'];

  return (
    <svg viewBox="0 0 200 320" width="160" height="260" style={{ filter: 'drop-shadow(0 8px 16px rgba(79,70,229,0.25))' }}>
      {/* Shadow on floor */}
      <ellipse cx="100" cy={P.shadow_y ?? 305} rx={P.shadow_rx ?? 36} ry="6" fill="rgba(0,0,0,0.18)" />

      {/* Legs (under torso so they sit behind) */}
      <path d={P.legs} fill="none" stroke={stroke} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
      {/* Torso */}
      <path d={P.torso} fill={fill} stroke={stroke} strokeWidth="3" strokeLinejoin="round" />
      {/* Arms */}
      <path d={P.arms} fill="none" stroke={stroke} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      {/* Head */}
      <circle cx={P.head[0]} cy={P.head[1]} r="20" fill={skin} stroke={stroke} strokeWidth="3" />
      {/* Equipment hint (ball/bag for throwing/coordination poses) */}
      {P.ball && <circle cx={P.ball[0]} cy={P.ball[1]} r="9" fill="#F59E0B" stroke="#92400E" strokeWidth="2" data-testid="model-ball" />}
    </svg>
  );
}

// Articulated body library. Each pose returns: head[cx,cy], torso (path),
// arms (multi-segment path: shoulder→elbow→hand each arm), legs, and optional
// shadow ellipse params and ball position.
const POSES_BY_KEY = {
  // STANDING NEUTRAL
  'stand': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L70,140 L65,180 M115,80 L130,140 L135,180',
    legs:  'M90,160 L88,240 L86,300 M110,160 L112,240 L114,300',
  },
  // STANDING WITH ARMS UP
  'stand-arms': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L70,30 L65,5 M115,80 L130,30 L135,5',
    legs:  'M90,160 L88,240 L86,300 M110,160 L112,240 L114,300',
  },
  // SINGLE-LEG HOP / LIFT
  'leg-lift-right': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L60,120 L50,180 M115,80 L140,120 L150,180',
    legs:  'M88,160 L86,250 L84,300 M110,160 L140,200 L160,230',
  },
  'leg-lift-hold': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L45,90 L20,100 M115,80 L155,90 L180,100',
    legs:  'M88,160 L86,250 L84,300 M110,160 L150,210 L170,240',
  },
  'leg-lift-arms': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L70,30 L65,10 M115,80 L130,30 L135,10',
    legs:  'M88,160 L86,250 L84,300 M110,160 L145,210 L165,240',
  },
  // SQUAT VARIATIONS
  'squat-low': {
    head: [100, 90],
    torso: 'M82,112 L118,112 L122,170 L78,170 Z',
    arms:  'M82,118 L50,140 L40,170 M118,118 L150,140 L160,170',
    legs:  'M82,170 L60,210 L40,260 L55,295 L75,300 M118,170 L140,210 L160,260 L145,295 L125,300',
    shadow_y: 305, shadow_rx: 50,
  },
  'squat-mid': {
    head: [100, 70],
    torso: 'M84,92 L116,92 L120,160 L80,160 Z',
    arms:  'M84,98 L60,130 L55,180 M116,98 L140,130 L145,180',
    legs:  'M85,160 L72,220 L80,290 M115,160 L128,220 L120,290',
  },
  // FLEXIBILITY / YOGA
  'quadruped': {
    head: [50, 130],
    torso: 'M70,150 L160,150 L170,180 L65,180 Z',
    arms:  'M75,160 L75,220 L70,280 M155,160 L160,220 L165,280',
    legs:  'M85,180 L100,230 L100,280 M150,180 L160,230 L165,280',
    shadow_y: 290, shadow_rx: 70,
  },
  'arch-up': {
    head: [50, 145],
    torso: 'M65,165 L160,135 L172,160 L70,190 Z',
    arms:  'M75,170 L75,235 L72,285 M150,150 L162,225 L165,285',
    legs:  'M85,185 L105,240 L102,285 M155,160 L162,225 L165,285',
    shadow_y: 290, shadow_rx: 70,
  },
  'arch-down': {
    head: [50, 115],
    torso: 'M68,150 L162,170 L170,195 L72,175 Z',
    arms:  'M78,160 L78,225 L75,285 M155,180 L165,235 L168,285',
    legs:  'M88,178 L108,230 L100,285 M152,193 L165,240 L165,285',
    shadow_y: 290, shadow_rx: 70,
  },
  'forward-fold': {
    head: [100, 180],
    torso: 'M85,200 L115,200 L120,160 L80,160 Z',
    arms:  'M86,200 L88,260 L90,300 M114,200 L112,260 L110,300',
    legs:  'M90,160 L88,240 L86,300 M110,160 L112,240 L114,300',
  },
  // CRAWLING
  'crawl-right': {
    head: [55, 135],
    torso: 'M70,155 L160,155 L168,182 L66,182 Z',
    arms:  'M75,162 L100,225 L130,275 M150,162 L155,225 L155,280',
    legs:  'M85,182 L115,235 L110,290 M150,182 L162,235 L168,290',
    shadow_y: 295, shadow_rx: 75,
  },
  'crawl-left': {
    head: [55, 135],
    torso: 'M70,155 L160,155 L168,182 L66,182 Z',
    arms:  'M75,162 L70,225 L70,280 M150,162 L130,225 L100,275',
    legs:  'M85,182 L80,235 L72,290 M150,182 L120,235 L100,290',
    shadow_y: 295, shadow_rx: 75,
  },
  // COORDINATION / SITTING
  'sit': {
    head: [70, 110],
    torso: 'M55,135 L85,135 L92,200 L48,200 Z',
    arms:  'M55,142 L40,180 L40,220 M85,142 L100,180 L100,220',
    legs:  'M55,200 L100,210 L160,210 L160,250 L55,250 Z',
    shadow_y: 300, shadow_rx: 70,
  },
  'sit-reach': {
    head: [80, 100],
    torso: 'M60,125 L92,128 L100,200 L52,200 Z',
    arms:  'M62,135 L120,150 L160,170 M92,135 L130,150 L165,170',
    legs:  'M55,200 L100,210 L160,210 L160,250 L55,250 Z',
    shadow_y: 300, shadow_rx: 70,
    ball: [165, 175],
  },
  'sit-throw': {
    head: [85, 95],
    torso: 'M62,120 L96,125 L104,200 L54,200 Z',
    arms:  'M62,132 L100,90 L150,80 M98,135 L130,150 L160,165',
    legs:  'M55,200 L100,210 L160,210 L160,250 L55,250 Z',
    shadow_y: 300, shadow_rx: 70,
    ball: [155, 78],
  },
  // SPORT-SKILL: THROWING
  'wind-up': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L60,40 L45,15 M115,80 L100,130 L80,170',
    legs:  'M88,160 L75,240 L65,300 M112,160 L130,240 L140,300',
    ball: [45, 15],
  },
  'throw-release': {
    head: [100, 50],
    torso: 'M85,72 L115,72 L120,160 L80,160 Z',
    arms:  'M85,80 L120,40 L160,30 M115,80 L130,130 L150,170',
    legs:  'M88,160 L100,240 L120,300 M112,160 L88,240 L70,300',
    ball: [165, 25],
  },
  'follow-through': {
    head: [100, 55],
    torso: 'M88,72 L118,72 L122,160 L82,160 Z',
    arms:  'M88,80 L140,80 L180,90 M118,80 L130,130 L150,170',
    legs:  'M90,160 L105,240 L125,300 M115,160 L90,240 L70,300',
  },
};

// Map (skill, stepIndex) → pose-key — used by Rotatable3D
// Uses fuzzy contains-match so AI-generated exercises with non-canonical
// skill labels (e.g. "مهارات التوازن والتنسيق") still pick the right pose set.
function resolveSkillKey(skill) {
  if (!skill) return 'تآزر';
  const s = String(skill);
  // Arabic substring checks
  if (s.includes('توازن') || s.includes('ثبات') || /balance/i.test(s)) return 'توازن';
  if (s.includes('قوة') || s.includes('قوّة') || /strength/i.test(s)) return 'قوة';
  if (s.includes('مرون') || /flexib|stretch/i.test(s)) return 'مرونة';
  if (s.includes('مكان') || /spatial|aware/i.test(s)) return 'إدراك مكاني';
  if (s.includes('رياضي') || s.includes('مهارة رياض') || /sport|skill/i.test(s)) return 'مهارة رياضية';
  if (s.includes('تآزر') || s.includes('تنسيق') || /coordin/i.test(s)) return 'تآزر';
  return 'تآزر';
}

function poseKeyForStep(skill, stepIndex, total) {
  const key = resolveSkillKey(skill);
  const set = POSE_SETS[key] || POSE_SETS['تآزر'];
  if (!set || set.length === 0) return 'stand';
  return set[stepIndex % set.length];
}
